import { z } from 'zod';

import { BOOKING_CANCEL_REASON_MAX_LENGTH } from '../booking-cancel/booking-cancel';

/**
 * Staff calling off a booking two members had already agreed (P25d).
 *
 * ## Why this is not `BookingCancelSchema` with the same field
 *
 * The member contract makes the reason **optional**, and says why: *"a member
 * who has changed their mind about meeting a stranger in person owes nobody an
 * explanation."* That argument is about a member's own meeting. This is an
 * **override** of two members' agreement, by somebody who is in neither side of
 * the match — the act most likely to be disputed later, and the one whose audit
 * row somebody will actually read. `reason: null` in that row is the trail
 * failing at exactly the moment it is needed, so the reason is required.
 *
 * It is genuinely narrative and cannot be an enum (U0): "the venue closed", "the
 * partner institution withdrew supervision", "a moderation action on one side".
 * An enum arm per cause would make staff pick the nearest wrong answer, which
 * then reads as data.
 *
 * The bound is the member contract's own constant rather than a second number,
 * so the two spellings of "how long a cancellation reason may be" cannot drift.
 * `.trim()` before the check, and a blank string is refused rather than stored —
 * "  " is not a reason.
 */
export const BookingStaffCancelSchema = z
  .object({
    reason: z.string().trim().min(1).max(BOOKING_CANCEL_REASON_MAX_LENGTH),
  })
  .strict();

export type BookingStaffCancelRequest = z.infer<typeof BookingStaffCancelSchema>;
